import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Button } from '@/components/ui/button';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { authService } from '@/services/auth';
import { getMarketingSiteOrigin } from '@/utils/hostOrigins';

const SignupPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { registerInvitedTeacher, registerInvitedStudent } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tokenValidationState, setTokenValidationState] = useState<'idle' | 'validating' | 'valid' | 'invalid'>('idle');

  const inviteType = searchParams.get('inviteType');
  const invitationToken = searchParams.get('invitationToken') ?? '';
  const isTeacherInviteFlow = inviteType === 'teacher' && invitationToken.length > 0;
  const isStudentInviteFlow = inviteType === 'student' && invitationToken.length > 0;
  const isInviteFlow = isTeacherInviteFlow || isStudentInviteFlow;

  useEffect(() => {
    // If already authenticated, redirect to dashboard
    if (authService.getToken()) {
      navigate('/');
      return;
    }

    if (isInviteFlow) {
      const validate = async () => {
        try {
          setTokenValidationState('validating');
          const validation = isTeacherInviteFlow
            ? await authService.validateTeacherInvitationToken(invitationToken)
            : await authService.validateStudentInvitationToken(invitationToken);
          const valid = !!validation.valid;
          setTokenValidationState(valid ? 'valid' : 'invalid');
          if (!valid) {
            setError('This invitation link is invalid, expired, or already used.');
            return;
          }
          setEmail(validation.email ?? '');
          setTenantName(validation.tenantName ?? '');
        } catch {
          setTokenValidationState('invalid');
          setError('This invitation link is invalid, expired, or already used.');
        }
      };
      validate();
      return;
    }

    // Default behavior for non-invitation signups: redirect to webapp signup
    const redirect = searchParams.get('redirect') || 'dashboard';
    const webappUrl = `${getMarketingSiteOrigin()}/signup?redirect=${redirect}`;
    window.location.href = webappUrl;
  }, [navigate, searchParams, invitationToken, isTeacherInviteFlow, isStudentInviteFlow, isInviteFlow]);

  const handleInviteSignup = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError('Password and confirmation do not match.');
      return;
    }
    setLoading(true);
    try {
      const result = isTeacherInviteFlow
        ? await registerInvitedTeacher(email, password, invitationToken, firstName, lastName)
        : await registerInvitedStudent(email, password, invitationToken, firstName, lastName);
      if (!result.success) {
        setError(result.error ?? 'Could not create your account');
        return;
      }
      navigate('/', { replace: true });
    } finally {
      setLoading(false);
    }
  };

  if (isInviteFlow) {
    return (
      <>
        <Helmet>
          <title>{isTeacherInviteFlow ? 'Teacher Invitation' : 'Student Invitation'} - Arrangelly Dashboard</title>
        </Helmet>
        <div className="min-h-screen bg-gradient-to-b from-slate-50 via-violet-50/30 to-slate-100 flex items-center justify-center px-4 py-16">
          <div className="w-full max-w-md bg-white/95 border border-slate-200 shadow-lg shadow-slate-200/50 rounded-2xl p-8 backdrop-blur-md">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
              {isTeacherInviteFlow ? 'Accept Teacher Invitation' : 'Accept Student Invitation'}
            </h1>
            <p className="text-sm text-slate-600 mb-6">
              {isTeacherInviteFlow
                ? 'Create your teacher account for the invited organization.'
                : 'Create your student account for the invited organization.'}
            </p>
            {tenantName && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 text-blue-900 rounded-lg text-sm">
                Invited by organization: <span className="font-semibold">{tenantName}</span>
              </div>
            )}

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                {error}
              </div>
            )}

            {tokenValidationState === 'validating' ? (
              <p className="text-slate-600">Validating invitation...</p>
            ) : tokenValidationState === 'invalid' ? (
              <p className="text-slate-600">Please contact your administrator for a new invitation.</p>
            ) : (
              <form onSubmit={handleInviteSignup} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-slate-600 mb-1" htmlFor="firstName">
                      First Name
                    </label>
                    <input
                      id="firstName"
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="w-full rounded-lg bg-white border border-slate-200 px-3 py-2 text-sm text-foreground"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-600 mb-1" htmlFor="lastName">
                      Last Name
                    </label>
                    <input
                      id="lastName"
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="w-full rounded-lg bg-white border border-slate-200 px-3 py-2 text-sm text-foreground"
                      placeholder="Doe"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-slate-600 mb-1" htmlFor="email">
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    readOnly
                    required
                    className="w-full rounded-lg bg-slate-100 border border-slate-200 px-3 py-2 text-sm text-slate-600"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-600 mb-1" htmlFor="password">
                    Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    className="w-full rounded-lg bg-white border border-slate-200 px-3 py-2 text-sm text-foreground"
                    placeholder="••••••••"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-600 mb-1" htmlFor="confirmPassword">
                    Confirm Password
                  </label>
                  <input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={8}
                    className="w-full rounded-lg bg-white border border-slate-200 px-3 py-2 text-sm text-foreground"
                    placeholder="••••••••"
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full">
                  {loading ? 'Creating account...' : `Create ${isTeacherInviteFlow ? 'teacher' : 'student'} account`}
                </Button>
              </form>
            )}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Sign Up - Arrangelly Dashboard</title>
      </Helmet>
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-600">Redirecting to signup...</p>
        </div>
      </div>
    </>
  );
};

export default SignupPage;

